import React, { useState, useContext, useEffect } from 'react';
import { Grid, TextField, DialogActions, Button, MenuItem, CircularProgress, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import styles from './styles.module.css';
import { AlertMessagesContext } from 'react-alert-messages';
import VariantService from 'services/VariantService';
import { useTranslation } from 'react-i18next';

export default function VariantLabelCreateDialog({
  open,
  handleClose,
  handleSuccess,
  variantOptions = [],
  initialVariantId = null,
}) {
  const { t: tCommon } = useTranslation('common');
  const { t: tPopup } = useTranslation('popup');
  const [data, setData] = useState({ variantId: '', name: '' });
  const [isLoading, setLoading] = useState(false);
  const { postAlertMessage } = useContext(AlertMessagesContext);

  useEffect(() => {
    if (open) {
      setData({ variantId: initialVariantId || '', name: '' });
    }
  }, [open, initialVariantId]);

  if (!open) return null;

  const handleCreateVariant = async () => {
    setLoading(true);
    try {
      const createdLabel = await VariantService.createVariantLabel({
        dimension_id: data.variantId,
        name: data.name,
      });
      handleClose();
      handleSuccess(createdLabel);
      postAlertMessage({
        type: 'success',
        text: tPopup('Label created.'),
      });
    } catch (err) {
      postAlertMessage({
        type: 'error',
        text: err.message || tPopup('Failed to create variant label.'),
      });
    }
    setLoading(false);
  };

  return (
    <>
      <div className={styles.popUp}>
        <div className={styles.contentWrapper}>
          <div className={styles.headSec}>
            <h2 className={styles.editTitle}>{tCommon('Add Variant Label')}</h2>
            <IconButton onClick={handleClose} className={styles.closeBtn}>
              <CloseIcon />
            </IconButton>
          </div>

          <div className={styles.inputSec}>
            <Grid container className={styles.inputRow} alignItems="center">
              <Grid item xs={4} sm={4} spacing={2} justify="flex-end" className={styles.inputLabelContainer}>
                <h3 className={styles.inputLabel}>{tCommon('Select Variant')}</h3>
              </Grid>
              <Grid item xs={8} sm={8}>
                <TextField
                  select
                  label={tCommon('Variant')}
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={data.variantId}
                  onChange={(e) => setData({ ...data, variantId: e.target.value })}
                >
                  {variantOptions.map((opt) => (
                    <MenuItem key={opt.id} value={opt.id}>
                      {opt.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>

            <Grid container className={styles.inputRow} alignItems="center">
              <Grid item xs={4} sm={4} spacing={2} justify="flex-end" className={styles.inputLabelContainer}>
                <h3 className={styles.inputLabel}>{tCommon('Variant Label Name')}</h3>
              </Grid>
              <Grid item xs={8} sm={8}>
                <TextField
                  label={tCommon('Variant Label Name')}
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={data.name}
                  onChange={(e) => setData({ ...data, name: e.target.value })}
                />
              </Grid>
            </Grid>

            <DialogActions>
              <Button variant="contained" color="secondary" size="small" onClick={handleClose} disabled={isLoading}>
                {tCommon('Close')}
              </Button>
              <Button
                variant="contained"
                color="primary"
                size="small"
                style={{ backgroundColor: '#00a65a' }}
                disabled={!data.variantId || !data.name.trim() || isLoading}
                onClick={handleCreateVariant}
              >
                {isLoading ? (
                  <>
                    <CircularProgress size={20} color="inherit" style={{ marginRight: '10px' }} />
                    {tCommon('Creating...')}
                  </>
                ) : (
                  tCommon('Create')
                )}
              </Button>
            </DialogActions>
          </div>
        </div>
      </div>
    </>
  );
}
